import * as React from 'react'
import type { Currency, CurrencyCode, Language } from 'codegen/types'
import { defaultLanguage } from 'cf-i18n/constants'

import type { Key } from '../i18n'
import { usePathname } from '../hooks/use-path-name'
import type { dynamic } from './dynamic'
import type { NavItem } from '../hooks/use-nav-items'

export type HeaderLinkProps = React.ComponentProps<'a'>

export interface HeaderData {
  lang: Language
  i18n: ((key: Key) => string) | ((key: Key, params?: Record<string, unknown>) => string)
  activeCurrency: Currency
  forceIsLoggedIn?: boolean
  forceIsAllAccess?: boolean
  isHomepage?: boolean
  navItemsOverride?: NavItem[]
  onLoginClick?: () => void
  onSignUpClick?: () => void
  onLogoutClick?: () => void
  onCurrencyChange: (currency: CurrencyCode) => void
  onLanguageChange: (language: Language) => void
  onLanguageOpen?: () => void
  onCurrencyOpen?: () => void
  utils?: {
    Link: React.ElementType<HeaderLinkProps>
    Image: React.ElementType
    router: {
      pathname: string
    }
    dynamic?: typeof dynamic
  }
}

type ContextProps = React.PropsWithChildren<HeaderData>

const HeaderContext = React.createContext<HeaderData>(null!)

export function HeaderContextProvider({ children, activeCurrency, ...rest }: ContextProps) {
  return (
    <HeaderContext.Provider
      value={{
        activeCurrency: activeCurrency,
        ...rest,
      }}
    >
      {React.useMemo(() => children, [children])}
    </HeaderContext.Provider>
  )
}

export function useHeaderContext() {
  return React.useContext(HeaderContext)
}

export function useLocale() {
  return useHeaderContext().lang
}

export function useActiveCurrency() {
  return useHeaderContext().activeCurrency
}

export function useForceIsLoggedIn() {
  return useHeaderContext().forceIsLoggedIn
}

export function useForceIsAllAccess() {
  return useHeaderContext().forceIsAllAccess
}

export function useTranslations() {
  return useHeaderContext().i18n
}

function LinkWithSameRouteReloading(props: HeaderLinkProps) {
  const { ref, ...restProps } = props
  const { utils } = useHeaderContext()

  if (restProps.href === utils?.router.pathname) {
    return <LanguageAwareLink ref={ref} {...restProps} />
  }

  const Component = utils?.Link || LanguageAwareLink

  if (restProps.href?.startsWith('http')) {
    return <Component ref={ref} target="_blank" {...restProps} />
  }

  return <Component ref={ref} {...restProps} />
}

export function useLinkComponent() {
  return LinkWithSameRouteReloading
}

export function useImageComponent() {
  return useHeaderContext().utils?.Image ?? 'img'
}

export function useRouterUtil() {
  const pathname = usePathname()
  return { pathname }
}

export function useOnLoginClick() {
  return useHeaderContext().onLoginClick
}

export function useOnSignUpClick() {
  return useHeaderContext().onSignUpClick
}

export function useOnLogoutClick() {
  return useHeaderContext().onLogoutClick
}

export function useOnLanguageOpen() {
  return useHeaderContext().onLanguageOpen
}

export function useOnCurrencyOpen() {
  return useHeaderContext().onCurrencyOpen
}

export function useOnCurrencyChange() {
  return useHeaderContext().onCurrencyChange
}

export function useOnLanguageChange() {
  return useHeaderContext().onLanguageChange
}

export function useDynamicLoader() {
  return useHeaderContext().utils?.dynamic
}

function DefaultLink(props: HeaderLinkProps) {
  const { ref, ...restProps } = props
  const locale = useLocale()
  const { href, ...rest } = restProps

  if (locale === defaultLanguage || href?.startsWith('http')) {
    return <a ref={ref} href={href} {...rest} />
  }

  return <a ref={ref} {...restProps} href={`/${locale}${href}`} hrefLang={locale} />
}
export const LanguageAwareLink = DefaultLink
